import { SupplierInput } from "@/app/Suppliers/Components/SupplierInput";
import { RelocationInclusionType } from "@/gql/graphql";
import { RelocationInclusionForm } from "@/app/Relocations/Forms/RelocationInclusionForm";
import { VehicleInput } from "@/app/Vehicles/Components/VehicleInput";
import { CurrencyInput } from "@/lib/Components/Form/Inputs/CurrencyInput";
import { RelocationReferencesInput } from "@/app/Relocations/Components/RelocationReferencesInput";
import { Suspense, useEffect, useId, useRef, useState } from "react";
import { GapRentalFormValues } from "@/app/GapRentals/Utils/getDefaultGapRentalValues";
import dayjs from "dayjs";
import { useSuspenseGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import { supplierRecordQuery } from "@/app/Suppliers/GraphQL/supplierRecordQuery";
import { SupplierOfficeInput } from "@/app/Offices/Components/SupplierOfficeInput";
import { NumberInput } from "@/lib/Components/Form/Inputs/NumberInput";
import { DistanceAllowedInput } from "@/lib/Components/Form/Inputs/DistanceAllowedInput";
import { StepperInput } from "@/lib/Components/Form/Inputs/StepperInput";
import { DateInput } from "@/lib/Components/Form/Inputs/DateInput";
import { SupplierInsuranceOptionInput } from "@/app/Suppliers/Components/SupplierInsuranceOptionInput";
import { supplierInsuranceOptionListQuery } from "@/app/Suppliers/GraphQL/supplierInsuranceOptionListQuery";
import { InfoIcon, TriangleAlertIcon } from "lucide-react";
import { useFormikContext } from "formik";
import { useTranslations } from "use-intl";
import { FormErrorSummary } from "@/app/Relocations/Components/FormErrorSummary";
import { FormSection } from "@/lib/Components/Form/Components/FormSection";
import { GapRentalDistanceMode } from "@/app/GapRentals/Utils/gapRentalDistance";
import { Skeleton } from "@/components/ui/skeleton";
import {
  currencyDecimals,
  formatCurrency,
} from "@/lib/Formatters/formatCurrency";
import { Input } from "@/components/catalyst/input";
import { Field, Label } from "@/components/catalyst/fieldset";
import { SegmentedButton } from "@/lib/Components/Button/SegmentedButton";
import { useFormEditState } from "@/lib/Components/Form/Hooks/useFormEditState";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

// Mirrors App\Relocations\Relocations\Data\GapRentalPricingData.
const IMOOVA_COMMISSION = 0.2;
// Below this discount the customer rate is too close to retail — warn.
const MIN_DISCOUNT = 0.2;

const DISCOUNT_PRESETS = [20, 25, 30];

function getDistanceModes(
  unitTypeLabel: string,
): { value: GapRentalDistanceMode; label: string }[] {
  return [
    { value: "UNLIMITED", label: "Unlimited" },
    { value: "PER_UNIT", label: `Per ${unitTypeLabel}` },
    { value: "FLAT", label: "Flat total" },
  ];
}

function BreakdownLabel({
  label,
  tooltip,
}: {
  label: string;
  tooltip: string;
}) {
  return (
    <Tooltip delayDuration={300}>
      <TooltipTrigger
        tabIndex={-1}
        type="button"
        className="flex items-center gap-1 text-left text-gray-500"
      >
        {label}
        <InfoIcon className="size-3 text-gray-400" />
      </TooltipTrigger>
      <TooltipContent>{tooltip}</TooltipContent>
    </Tooltip>
  );
}

/**
 * Sits beside the field label rather than under the input, so the warning
 * reads as part of the field instead of a stray icon in the layout.
 */
function DateLabel({ label, warning }: { label: string; warning?: string }) {
  return (
    <span className="flex items-center gap-1.5">
      {label}
      {warning ? (
        <Tooltip delayDuration={300}>
          <TooltipTrigger tabIndex={-1} type="button" className="shrink-0">
            <TriangleAlertIcon className="size-3.5 text-amber-500" />
          </TooltipTrigger>
          <TooltipContent>{warning}</TooltipContent>
        </Tooltip>
      ) : null}
    </span>
  );
}

function GapRentalFieldsSkeleton() {
  return (
    <div className="col-span-full grid grid-cols-6 gap-4">
      <div className="col-span-3 space-y-2">
        <Skeleton className="h-4 w-16" />
        <Skeleton className="h-10" />
      </div>
      <div className="col-span-3 space-y-2">
        <Skeleton className="h-4 w-16" />
        <Skeleton className="h-10" />
      </div>
    </div>
  );
}

export function GapRentalForm({
  // The record screen edits inclusions in their own card; the create dialog has
  // no such card, so it asks for them inline.
  showInclusions = false,
}: {
  showInclusions?: boolean;
}) {
  const { values, initialValues } = useFormikContext<GapRentalFormValues>();

  return (
    <>
      {!initialValues.supplier?.connect ? (
        <div className="col-span-full mb-6 border-l-4 border-blue-500 pl-4">
          <SupplierInput name="supplier.connect" label="Supplier" />
        </div>
      ) : null}

      {values.supplier.connect ? (
        <Suspense fallback={<GapRentalFieldsSkeleton />}>
          <FieldsWithSupplier showInclusions={showInclusions} />
        </Suspense>
      ) : null}
    </>
  );
}

function FieldsWithSupplier({ showInclusions }: { showInclusions: boolean }) {
  const { values, initialValues, setFieldValue, setFieldTouched, setValues } =
    useFormikContext<GapRentalFormValues>();
  const { isEditing } = useFormEditState();
  const t = useTranslations("relocation");
  const tForm = useTranslations("relocation.form");

  if (!values.supplier.connect) {
    throw new Error("A supplier is required");
  }

  const {
    data: { supplier },
  } = useSuspenseGqlQuery(supplierRecordQuery, {
    id: values.supplier.connect,
  });

  const { data: insuranceOptionsData } = useSuspenseGqlQuery(
    supplierInsuranceOptionListQuery,
    {
      supplierId: values.supplier.connect,
      page: 1,
      first: 20,
    },
  );

  const defaultInsuranceOption =
    insuranceOptionsData.supplierInsuranceOptions.data.find(
      (opt) => opt.is_default,
    );

  // Both queries above suspend on the selected supplier, so by the time this
  // runs the default insurance option always belongs to the current supplier.
  // Applied when the supplier changes, and on mount for a new gap rental that
  // opened with a supplier already selected (no insurance picked yet) — an
  // existing record keeps its saved values, and clearing insurance afterwards
  // is respected.
  useEffect(() => {
    const isInitialSupplier = supplier.id === initialValues.supplier?.connect;
    const hasInsurance = !!initialValues.supplierInsuranceOption?.connect;

    if (isInitialSupplier && hasInsurance) return;

    const updates: Partial<GapRentalFormValues> = {
      hire_unit_type: supplier.hire_unit_type,
      currency: supplier.currency,
      measurement: supplier.measurement,
      excess_distance_rate: supplier.excess_distance_rate ?? null,
      supplierInsuranceOption: {
        connect: defaultInsuranceOption?.id ?? null!,
      },
    };

    setValues((old) => ({ ...old, ...updates }));
  }, [supplier.id]);

  const currency = values.currency;

  // Suppliers talk in kms or mileage depending on their measurement setting.
  const distanceLabel = t(`distance_allowed_label.${values.measurement}`);
  const distanceUnit = t(`distanceUnit.${values.measurement}`);
  const unitTypeLabel = t(`hire_unit_type.${values.hire_unit_type}`);
  const unitTypePluralLabel = t(
    `hire_unit_type_plural.${values.hire_unit_type}`,
  );

  // The retail rate and a % discount are entered; the advertised (customer)
  // rate is derived from them. Both are submitted — the discount is what gets
  // advertised, so it is stored rather than re-derived from the rounded rate.
  // Null until a discount is picked: a new gap rental starts with no preset
  // selected so the advertised rate is always a deliberate choice.
  const discountPercent = values.discount_percent ?? NaN;
  const [isCustomDiscount, setIsCustomDiscount] = useState(
    () =>
      initialValues.discount_percent !== null &&
      initialValues.discount_percent !== undefined &&
      !DISCOUNT_PRESETS.includes(initialValues.discount_percent),
  );

  const discountInputId = useId();

  const applyDiscount = (retail: number | null, percent: number) => {
    if (!Number.isFinite(percent)) return;

    setFieldValue("discount_percent", percent);

    if (retail === null || retail <= 0) return;
    // Rates are stored in the currency's minor unit — round the discounted
    // rate to a whole major unit (whole dollars/yen/...) so the advertised
    // price is a round number. Round DOWN so the rounded rate never exceeds
    // the discount ceiling that advertisedRateRefine enforces (rounding up
    // could push a preset deal just past retail*(1-MIN_DISCOUNT) and block
    // submit with no in-form warning).
    // Mirrors GapRentalPricingData::fromDiscountPercent, which recomputes this
    // on save — the two must agree or the preview lies.
    const factor = 10 ** currencyDecimals[values.currency];
    setFieldValue(
      "extra_hire_unit_rate",
      Math.floor((retail * (100 - percent)) / 100 / factor) * factor,
    );
  };

  const retailRate = values.retail_rate ?? 0;
  const customerRate = values.extra_hire_unit_rate ?? 0;
  const showDiscountError =
    retailRate > 0 && discountPercent < MIN_DISCOUNT * 100;
  const imoovaCommission = Math.round(customerRate * IMOOVA_COMMISSION);
  const supplierReceives = customerRate - imoovaCommission;

  // Gap rentals run hire_units_allowed=0, so the whole bookable window lives in
  // extra_hire_units_allowed — derive it from the available date range.
  const gapRentalUnitsFromDates = (
    from: string | null | undefined,
    to: string | null | undefined,
  ) => {
    if (!from || !to) return 0;
    return Math.max(0, dayjs(to).diff(dayjs(from), "day"));
  };

  // The window is also the ceiling for minimum hire, so shrinking the dates
  // pulls a now-unbookable minimum down with them.
  const applyDateRange = async (
    from: string | null | undefined,
    to: string | null | undefined,
  ) => {
    const units = gapRentalUnitsFromDates(from, to);

    await setFieldValue("extra_hire_units_allowed", units);

    if (units > 0 && values.minimum_hire_units > units) {
      await setFieldValue("minimum_hire_units", units);
    }
  };

  const distanceMode = values.distance_mode ?? "UNLIMITED";

  // Picking a capped mode focuses its input so the number can be typed straight
  // away. The token remounts the input on every click, including a click on the
  // mode that is already selected, since autoFocus only fires on mount.
  const [distanceFocusToken, setDistanceFocusToken] = useState(0);

  const applyDistanceMode = async (mode: GapRentalDistanceMode) => {
    if (mode !== "UNLIMITED") {
      setDistanceFocusToken((token) => token + 1);
    }

    // Switching modes blurs the input that is going away, which would mark it
    // touched and flash its error before anything was even entered.
    await setFieldTouched("distance_value", false, false);

    await setValues((old) => ({
      ...old,
      distance_mode: mode,
      // The number carries across modes; a capped mode picked with nothing
      // entered starts from the supplier's own per-unit default.
      distance_value:
        mode === "PER_UNIT"
          ? (old.distance_value ??
            supplier.extra_distance_allowed_per_extra_hire_unit ??
            null)
          : (old.distance_value ?? null),
      // Unlimited clears the excess rate on submit, so a capped mode picked
      // afterwards starts from the supplier's own excess rate again.
      excess_distance_rate:
        mode === "UNLIMITED"
          ? old.excess_distance_rate
          : (old.excess_distance_rate ?? supplier.excess_distance_rate ?? null),
    }));
  };

  // An existing gap rental shows everything at once; a new one is revealed a
  // section at a time so the next decision is always the one on screen.
  const isExistingRecord = !!initialValues.vehicle?.connect;

  // Sections mounted on the first render are where the user already is, so only
  // the ones revealed later pull focus.
  const hasMounted = useRef(false);
  useEffect(() => {
    hasMounted.current = true;
  }, []);

  const hasPickup =
    !!values.vehicle?.connect && !!values.departureOffice?.connect;
  const hasAvailability =
    hasPickup && !!values.available_from_date && !!values.available_to_date;
  // The advertised rate only exists once a discount has been chosen.
  const hasPrice =
    hasAvailability &&
    (values.retail_rate ?? 0) > 0 &&
    (values.extra_hire_unit_rate ?? 0) > 0;
  const hasDistance =
    hasPrice && (distanceMode === "UNLIMITED" || values.distance_value != null);
  const hasCover = hasDistance && !!values.supplierInsuranceOption?.connect;

  const isRevealed = (isSectionComplete: boolean) =>
    isExistingRecord || isSectionComplete;

  return (
    <div className="col-span-full space-y-8">
      <FormErrorSummary />

      <div className="space-y-8">
        <FormSection
          step={1}
          title="Vehicle & pickup"
          isComplete={hasPickup}
          description={
            hasPickup ? undefined : "Where the rental starts and ends"
          }
        >
          <div className="grid grid-cols-3 gap-4 sm:grid-cols-6">
            <SupplierOfficeInput
              name="departureOffice.connect"
              label="Office (collection & return)"
              className="col-span-3"
              getQueryVariables={(search) => ({
                first: 20,
                page: 1,
                search,
                supplier_id: values.supplier?.connect,
                archived: false,
              })}
              initialFixtureValues={{
                supplier: {
                  connect: values.supplier.connect,
                },
              }}
              onChange={(office) => {
                if (office) {
                  setFieldValue("deliveryOfficeIds", [office.id]);
                }
              }}
            />

            <VehicleInput
              name="vehicle.connect"
              label="Vehicle"
              className="col-span-2"
              getQueryVariables={(search) => ({
                first: 20,
                page: 1,
                search,
                supplier_id: values.supplier?.connect,
              })}
              initialFixtureValues={{
                supplier: {
                  connect: values.supplier?.connect ?? null,
                },
              }}
              onChange={async (vehicle) => {
                if (!vehicle) {
                  return;
                }

                await setFieldValue("hire_unit_type", vehicle.hire_unit_type);
              }}
            />

            <StepperInput
              min={1}
              name={"quantity"}
              label="Quantity"
              className="col-span-1"
            />
          </div>
        </FormSection>

        {isRevealed(hasPickup) ? (
          <FormSection
            step={2}
            title="Availability"
            isComplete={hasAvailability}
            focusOnMount={hasMounted.current}
          >
            <div className="grid grid-cols-3 gap-4 sm:grid-cols-12">
              <div className="col-span-3">
                <DateInput
                  name={"available_from_date"}
                  label={
                    <DateLabel
                      label="Available from"
                      warning={
                        values.available_from_date &&
                        dayjs(values.available_from_date).isBefore(
                          dayjs(),
                          "day",
                        )
                          ? "In the past — pick-up can only start from today"
                          : undefined
                      }
                    />
                  }
                  min={dayjs().local().format("YYYY-MM-DD")}
                  max={dayjs().local().add(1, "year").format("YYYY-MM-DD")}
                  onChange={async (value) => {
                    if (
                      value &&
                      dayjs(value).isAfter(values.available_to_date)
                    ) {
                      await setFieldValue("available_to_date", null);
                      await applyDateRange(value, null);
                      return;
                    }

                    await applyDateRange(value, values.available_to_date);
                  }}
                />
              </div>

              <div className="col-span-3">
                <DateInput
                  name={"available_to_date"}
                  label={
                    <DateLabel
                      label="Available to"
                      warning={
                        values.available_to_date &&
                        dayjs(values.available_to_date).isBefore(dayjs(), "day")
                          ? "In the past — this gap rental is no longer bookable"
                          : undefined
                      }
                    />
                  }
                  min={(values.available_from_date
                    ? dayjs(values.available_from_date)
                    : dayjs()
                  )
                    .local()
                    .format("YYYY-MM-DD")}
                  max={dayjs().local().add(1, "year").format("YYYY-MM-DD")}
                  onChange={async (value) => {
                    await applyDateRange(values.available_from_date, value);
                  }}
                />
              </div>

              <NumberInput
                name="minimum_hire_units"
                className="col-span-3"
                label="Minimum hire"
                suffix={unitTypePluralLabel}
                inputProps={{
                  min: 1,
                  max: values.extra_hire_units_allowed || undefined,
                }}
              />
            </div>
          </FormSection>
        ) : null}

        {isRevealed(hasAvailability) ? (
          <FormSection
            step={3}
            title="Price"
            isComplete={hasPrice}
            focusOnMount={hasMounted.current}
          >
            <div className="grid grid-cols-3 gap-4 sm:grid-cols-6">
              <CurrencyInput
                name="retail_rate"
                label="Retail rate"
                tooltip="The supplier's standard daily rate."
                currency={currency}
                isEditable
                suffix={`per ${unitTypeLabel}`}
                className="col-span-3"
                onChange={(value) => {
                  applyDiscount(value, discountPercent);
                }}
              />
              <Field className="col-span-3">
                <Label htmlFor={discountInputId}>Discount off retail</Label>
                {isEditing ? (
                  <div className="space-y-2">
                    <div className="inline-flex w-full rounded-lg bg-gray-100 p-1">
                      {DISCOUNT_PRESETS.map((preset) => (
                        <SegmentedButton
                          key={preset}
                          isSelected={
                            !isCustomDiscount && discountPercent === preset
                          }
                          onClick={() => {
                            setIsCustomDiscount(false);
                            applyDiscount(values.retail_rate ?? null, preset);
                          }}
                        >
                          {`${preset}%`}
                        </SegmentedButton>
                      ))}
                      <SegmentedButton
                        isSelected={isCustomDiscount}
                        onClick={() => setIsCustomDiscount(true)}
                      >
                        Custom
                      </SegmentedButton>
                    </div>

                    {isCustomDiscount ? (
                      <span className="relative block w-full [&_input::-webkit-inner-spin-button]:appearance-none [&_input::-webkit-outer-spin-button]:appearance-none [&_input]:appearance-none">
                        <Input
                          id={discountInputId}
                          type="number"
                          autoFocus
                          onFocus={(event) => event.target.select()}
                          min={0}
                          max={90}
                          step={1}
                          value={
                            Number.isFinite(discountPercent)
                              ? discountPercent
                              : ""
                          }
                          onChange={(event) => {
                            // Whole percents only — the discount is stored in
                            // an Int column and advertised as a whole number.
                            const percent =
                              event.target.value === ""
                                ? NaN
                                : Math.min(
                                    90,
                                    Math.max(
                                      0,
                                      Math.round(
                                        parseFloat(event.target.value),
                                      ),
                                    ),
                                  );

                            if (Number.isNaN(percent)) {
                              setFieldValue("discount_percent", null);
                              return;
                            }

                            applyDiscount(values.retail_rate ?? null, percent);
                          }}
                        />
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex max-w-[50%] items-center pr-3">
                          <span className="truncate text-sm text-zinc-500">
                            % off retail
                          </span>
                        </div>
                      </span>
                    ) : null}
                  </div>
                ) : (
                  <div className="mt-[2px] border-t border-gray-200">
                    <div className="w-full pb-[5px] pt-2 text-sm text-gray-700">
                      {Number.isFinite(discountPercent)
                        ? `${Math.round(discountPercent)}% off retail`
                        : "--"}
                    </div>
                  </div>
                )}
              </Field>
            </div>

            {showDiscountError ? (
              <div className="flex items-start gap-2 rounded-md bg-red-50 p-3 text-sm text-red-800">
                <TriangleAlertIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-500" />
                <span>
                  The discount is only {Math.round(discountPercent)}% off
                  retail. Gap rentals must be at least{" "}
                  {Math.round(MIN_DISCOUNT * 100)}% off.
                </span>
              </div>
            ) : null}

            {/* Pricing breakdown */}
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
              <dl className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <dt>
                    <BreakdownLabel
                      label="Advertised rate"
                      tooltip="The full amount the customer pays."
                    />
                  </dt>
                  <dd className="font-medium text-gray-900">
                    {formatCurrency(customerRate, currency)}/{unitTypeLabel}
                  </dd>
                </div>
                <div className="my-1 border-t border-gray-200" />
                <div className="flex justify-between">
                  <dt>
                    <BreakdownLabel
                      label="Imoova commission (20%)"
                      tooltip="20% commission charged to the customer at the time of booking."
                    />
                  </dt>
                  <dd className="font-medium text-gray-900">
                    {formatCurrency(imoovaCommission, currency)}/{unitTypeLabel}
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt>
                    <BreakdownLabel
                      label={`${supplier.name} receives`}
                      tooltip={`The remaining 80% collected by ${supplier.name} at pickup.`}
                    />
                  </dt>
                  <dd className="font-medium text-gray-900">
                    {formatCurrency(supplierReceives, currency)}/{unitTypeLabel}
                  </dd>
                </div>
              </dl>
            </div>
          </FormSection>
        ) : null}

        {isRevealed(hasPrice) ? (
          <FormSection
            step={4}
            title="Distance"
            isComplete={hasDistance}
            focusOnMount={hasMounted.current}
          >
            <div className="grid grid-cols-3 gap-4 sm:grid-cols-6">
              {isEditing ? (
                <Field className="col-span-full">
                  <Label>{distanceLabel}</Label>
                  <div className="inline-flex w-full rounded-lg bg-gray-100 p-1">
                    {getDistanceModes(unitTypeLabel).map((mode) => (
                      <SegmentedButton
                        key={mode.value}
                        isSelected={distanceMode === mode.value}
                        onClick={() => applyDistanceMode(mode.value)}
                      >
                        {mode.label}
                      </SegmentedButton>
                    ))}
                  </div>
                </Field>
              ) : null}

              {distanceMode === "UNLIMITED" ? null : (
                <DistanceAllowedInput
                  name="distance_value"
                  label={
                    distanceMode === "PER_UNIT"
                      ? `${distanceLabel} per ${unitTypeLabel}`
                      : `${distanceLabel} in total`
                  }
                  tooltip={
                    distanceMode === "PER_UNIT"
                      ? `Included for each ${unitTypeLabel} of the hire.`
                      : `Included for the whole hire, however many ${unitTypeLabel}s are booked.`
                  }
                  measurement={values.measurement}
                  suffix={
                    distanceMode === "PER_UNIT" ? `/${unitTypeLabel}` : "total"
                  }
                  placeholder="Enter a distance"
                  className="col-span-3"
                  key={`${distanceMode}-${distanceFocusToken}`}
                  autoFocus={distanceFocusToken > 0}
                />
              )}

              {distanceMode === "UNLIMITED" && !isEditing ? (
                <DistanceAllowedInput
                  name="distance_value"
                  label={distanceLabel}
                  measurement={values.measurement}
                  className="col-span-3"
                />
              ) : null}

              {distanceMode === "UNLIMITED" ? null : (
                <CurrencyInput
                  name="excess_distance_rate"
                  label={tForm("excessRateLabel", {
                    label: t(
                      `extra_distance_label.${values.measurement}`,
                    ).toLowerCase(),
                  })}
                  tooltip={tForm("excessRateTooltip")}
                  currency={currency}
                  suffix={`/${distanceUnit}`}
                  className="col-span-3"
                />
              )}
            </div>
          </FormSection>
        ) : null}

        {isRevealed(hasDistance) ? (
          <FormSection
            step={5}
            title="Cover & plates"
            isComplete={hasCover}
            focusOnMount={hasMounted.current}
          >
            <div className="grid grid-cols-3 gap-4 sm:grid-cols-6">
              <SupplierInsuranceOptionInput
                name="supplierInsuranceOption.connect"
                label="Insurance"
                className="col-span-3"
                getQueryVariables={(search) => ({
                  search,
                  page: 1,
                  first: 20,
                  supplierId: values.supplier?.connect,
                })}
                initialFixtureValues={{
                  supplier: values.supplier,
                }}
              />

              <RelocationReferencesInput
                name="lines"
                label="License plates"
                placeholder="Add a license plate"
                optionalLabel
                max={values.quantity || undefined}
                className="col-span-3"
              />
            </div>
          </FormSection>
        ) : null}

        {showInclusions && isRevealed(hasCover) ? (
          <FormSection
            step={6}
            title="Extras"
            description="Optional"
            focusOnMount={hasMounted.current}
          >
            <RelocationInclusionForm
              excludedTypes={[
                RelocationInclusionType.Ferry,
                RelocationInclusionType.Fuel,
              ]}
            />
          </FormSection>
        ) : null}
      </div>
    </div>
  );
}
